import { useState, useRef, useEffect } from 'react';
import { Wrench, Send, X, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

const STARTERS = [
    'Rough idle / misfire',
    'Brake squeal',
    'Check engine light on',
    'Boost leak / underboost',
    'Overheating',
];

export function AiMechanicWidget() {
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const scroller = useRef(null);

    useEffect(() => {
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
    }, [messages, open]);

    const send = async (text) => {
        const t = (text || input).trim();
        if (!t) return;
        setMessages((m) => [...m, { role: 'user', content: t }]);
        setInput('');
        setSending(true);
        try {
            const { data } = await api.post('/chat', { session_id: sessionId, message: t });
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { role: 'assistant', content: '⚠️ Mechanic AI is offline right now. Please try again.' }]);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {!open && (
                <button
                    data-testid="ai-mechanic-open-button"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-16 md:bottom-20 right-4 z-50 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[var(--rrm-red)] text-white shadow-[0_10px_30px_rgba(225,29,72,0.45)] hover:bg-[var(--rrm-red2)] active:scale-[0.98]"
                >
                    <Wrench className="w-4 h-4" />
                    <span className="text-sm font-semibold">AI Mechanic</span>
                </button>
            )}
            {open && (
                <div className="fixed bottom-16 md:bottom-20 right-2 md:right-4 z-50 w-[min(96vw,420px)] h-[min(80vh,640px)] rrm-panel flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 inline-flex items-center justify-center">
                                <Wrench className="w-4 h-4 text-amber-400" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold leading-none">RRM Mechanic AI</div>
                                <div className="text-[10px] text-white/50 mt-0.5">Powered by GPT-4o</div>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/5" data-testid="ai-mechanic-close-button">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div ref={scroller} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                        {messages.length === 0 ? (
                            <div className="space-y-3">
                                <div className="text-sm text-white/70">
                                    Hi — I'm RRM Mechanic AI. Describe your symptom or pick a starter:
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {STARTERS.map((s) => (
                                        <button
                                            key={s}
                                            data-testid="ai-mechanic-suggestion-chip"
                                            onClick={() => send(s)}
                                            className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10"
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            messages.map((m, i) => (
                                <div
                                    key={m.created_at ? `${m.role}-${m.created_at}-${i}` : `${m.role}-${i}-${m.content.length}`}
                                    className={`text-sm leading-relaxed whitespace-pre-wrap rounded-lg px-3 py-2 border ${
                                        m.role === 'user'
                                            ? 'bg-white/5 border-white/10 self-end'
                                            : 'bg-amber-500/8 border-amber-500/20'
                                    }`}
                                >
                                    {m.content}
                                </div>
                            ))
                        )}
                        {sending && (
                            <div className="text-xs text-white/50 inline-flex items-center gap-2">
                                <Loader2 className="w-3 h-3 animate-spin" /> mechanic thinking…
                            </div>
                        )}
                    </div>
                    <div className="border-t border-white/10 p-2 flex items-center gap-2">
                        <input
                            data-testid="ai-mechanic-message-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && send()}
                            placeholder="Describe your symptom..."
                            className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                        />
                        <button
                            data-testid="ai-mechanic-send-button"
                            onClick={() => send()}
                            disabled={sending || !input.trim()}
                            className="px-3 py-2 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white inline-flex items-center gap-1 disabled:opacity-50"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
